/**
 * Projects / folders / workspaces storage in IndexedDB.
 * Current project and workspace IDs are stored in localStorage.
 * Single store with key prefixes: p:, f:, w:, file:
 */

import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import { getNonDeletedElements } from "@excalidraw/element";
import { createStore, get, set, del, keys, getMany } from "idb-keyval";
import { nanoid } from "nanoid";

import { isInitializedImageElement } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { FileId } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";

import { STORAGE_KEYS } from "../../app_constants";

import type {
  Project,
  ProjectId,
  Folder,
  FolderId,
  Workspace,
  WorkspaceId,
  WorkspaceScene,
} from "./types";

const store = createStore(`${STORAGE_KEYS.IDB_PROJECTS}-db`, "projects-store");

const PREFIX = { project: "p:", folder: "f:", workspace: "w:", file: "file:" };

export function getCurrentProjectId(): ProjectId | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_CURRENT_PROJECT_ID);
  } catch {
    return null;
  }
}

export function getCurrentWorkspaceId(): WorkspaceId | null {
  try {
    return localStorage.getItem(
      STORAGE_KEYS.LOCAL_STORAGE_CURRENT_WORKSPACE_ID,
    );
  } catch {
    return null;
  }
}

export function setCurrentWorkspace(
  projectId: ProjectId,
  workspaceId: WorkspaceId,
): void {
  try {
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_CURRENT_PROJECT_ID,
      projectId,
    );
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_CURRENT_WORKSPACE_ID,
      workspaceId,
    );
  } catch (e) {
    console.error(e);
  }
}

export function clearCurrentWorkspace(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_CURRENT_PROJECT_ID);
    localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_CURRENT_WORKSPACE_ID);
  } catch (e) {
    console.error(e);
  }
}

export function hasCurrentWorkspace(): boolean {
  return !!(getCurrentProjectId() && getCurrentWorkspaceId());
}

// ---------- Projects ----------

export async function listProjects(): Promise<Project[]> {
  const ks = await keys(store);
  const projectKeys = (ks as string[]).filter((k) =>
    k.startsWith(PREFIX.project),
  );
  const list = await getMany(projectKeys, store).then(
    (arr) => arr.filter(Boolean) as Project[],
  );
  return list.sort((a, b) => a.createdAt - b.createdAt);
}

export async function getProject(id: ProjectId): Promise<Project | null> {
  return (await get<Project>(PREFIX.project + id, store)) ?? null;
}

export async function createProject(name: string): Promise<Project> {
  const project: Project = {
    id: nanoid(),
    name: name.trim() || "Untitled project",
    createdAt: Date.now(),
  };
  await set(PREFIX.project + project.id, project, store);
  return project;
}

export async function updateProject(
  id: ProjectId,
  updates: { name?: string },
): Promise<Project | null> {
  const project = await get<Project>(PREFIX.project + id, store);
  if (!project) {
    return null;
  }
  const updated = { ...project, ...updates };
  if (updates.name !== undefined) {
    updated.name = updates.name.trim() || project.name;
  }
  await set(PREFIX.project + id, updated, store);
  return updated;
}

export async function deleteProject(id: ProjectId): Promise<void> {
  const folders = await listFolders(id);
  for (const folder of folders) {
    await deleteFolder(folder.id);
  }
  const workspaces = await listWorkspaces(id, null);
  for (const ws of workspaces) {
    await deleteWorkspace(ws.id);
  }
  await del(PREFIX.project + id, store);
  if (getCurrentProjectId() === id) {
    clearCurrentWorkspace();
  }
}

// ---------- Folders ----------

export async function listFolders(
  projectId: ProjectId,
  parentId: FolderId | null = null,
): Promise<Folder[]> {
  const ks = await keys(store);
  const folderKeys = (ks as string[]).filter((k) =>
    k.startsWith(PREFIX.folder),
  );
  const all = await getMany(folderKeys, store).then(
    (arr) => arr.filter(Boolean) as Folder[],
  );
  return all
    .filter((f) => f.projectId === projectId && f.parentId === parentId)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function createFolder(
  projectId: ProjectId,
  name: string,
  parentId: FolderId | null = null,
): Promise<Folder> {
  const folder: Folder = {
    id: nanoid(),
    projectId,
    name: name.trim() || "Untitled folder",
    parentId,
    createdAt: Date.now(),
  };
  await set(PREFIX.folder + folder.id, folder, store);
  return folder;
}

export async function updateFolder(
  id: FolderId,
  updates: { name?: string; parentId?: FolderId | null },
): Promise<Folder | null> {
  const folder = await get<Folder>(PREFIX.folder + id, store);
  if (!folder) {
    return null;
  }
  const updated = { ...folder, ...updates };
  if (updates.name !== undefined) {
    updated.name = updates.name.trim() || folder.name;
  }
  await set(PREFIX.folder + id, updated, store);
  return updated;
}

export async function deleteFolder(id: FolderId): Promise<void> {
  const folder = await get<Folder>(PREFIX.folder + id, store);
  if (!folder) {
    return;
  }
  const children = await listFolders(folder.projectId, id);
  for (const child of children) {
    await deleteFolder(child.id);
  }
  const workspaces = await listWorkspaces(folder.projectId, id);
  for (const ws of workspaces) {
    await deleteWorkspace(ws.id);
  }
  await del(PREFIX.folder + id, store);
}

// ---------- Workspaces ----------

export async function listWorkspaces(
  projectId: ProjectId,
  folderId: FolderId | null,
): Promise<Workspace[]> {
  const ks = await keys(store);
  const workspaceKeys = (ks as string[]).filter((k) =>
    k.startsWith(PREFIX.workspace),
  );
  const all = await getMany(workspaceKeys, store).then(
    (arr) => arr.filter(Boolean) as Workspace[],
  );
  return all
    .filter((w) => w.projectId === projectId && w.folderId === folderId)
    .sort((a, b) => a.updatedAt - b.updatedAt);
}

export async function getWorkspace(id: WorkspaceId): Promise<Workspace | null> {
  return (await get<Workspace>(PREFIX.workspace + id, store)) ?? null;
}

export async function createWorkspace(
  projectId: ProjectId,
  folderId: FolderId | null,
  name: string,
  scene?: WorkspaceScene,
): Promise<Workspace> {
  const now = Date.now();
  const workspace: Workspace = {
    id: nanoid(),
    projectId,
    folderId,
    name: name.trim() || "Untitled",
    elements: scene?.elements ?? [],
    appState: scene?.appState ?? {},
    updatedAt: now,
    createdAt: now,
  };
  await set(PREFIX.workspace + workspace.id, workspace, store);
  return workspace;
}

export async function updateWorkspaceScene(
  id: WorkspaceId,
  elements: readonly ExcalidrawElement[],
  appState: AppState,
): Promise<Workspace | null> {
  const workspace = await get<Workspace>(PREFIX.workspace + id, store);
  if (!workspace) {
    return null;
  }
  const clearedAppState = clearAppStateForLocalStorage(appState);
  const updated: Workspace = {
    ...workspace,
    elements: getNonDeletedElements(elements) as ExcalidrawElement[],
    appState: clearedAppState,
    updatedAt: Date.now(),
  };
  await set(PREFIX.workspace + id, updated, store);
  return updated;
}

export async function updateWorkspace(
  id: WorkspaceId,
  updates: { name?: string; folderId?: FolderId | null },
): Promise<Workspace | null> {
  const workspace = await get<Workspace>(PREFIX.workspace + id, store);
  if (!workspace) {
    return null;
  }
  const updated = { ...workspace, ...updates };
  if (updates.name !== undefined) {
    updated.name = updates.name.trim() || workspace.name;
  }
  await set(PREFIX.workspace + id, updated, store);
  return updated;
}

export async function deleteWorkspace(id: WorkspaceId): Promise<void> {
  await clearWorkspaceFiles(id);
  await del(PREFIX.workspace + id, store);
  if (getCurrentWorkspaceId() === id) {
    clearCurrentWorkspace();
  }
}

// ---------- Load / save current workspace scene ----------

export async function loadCurrentWorkspaceScene(): Promise<WorkspaceScene | null> {
  const projectId = getCurrentProjectId();
  const workspaceId = getCurrentWorkspaceId();
  if (!projectId || !workspaceId) {
    return null;
  }
  const workspace = await getWorkspace(workspaceId);
  if (!workspace || workspace.projectId !== projectId) {
    return null;
  }
  return { elements: workspace.elements, appState: workspace.appState };
}

export async function saveCurrentWorkspaceScene(
  elements: readonly ExcalidrawElement[],
  appState: AppState,
): Promise<boolean> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) {
    return false;
  }
  await updateWorkspaceScene(workspaceId, elements, appState);
  return true;
}

/** Save current workspace scene + images. Call from onChange when in workspace mode. */
export async function saveCurrentWorkspaceData(
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  files: BinaryFiles,
): Promise<boolean> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) {
    return false;
  }
  await updateWorkspaceScene(workspaceId, elements, appState);
  const addedFiles = new Map<FileId, BinaryFileData>();
  for (const el of elements) {
    if (isInitializedImageElement(el) && files[el.fileId]) {
      addedFiles.set(el.fileId, files[el.fileId]);
    }
  }
  if (addedFiles.size) {
    await saveWorkspaceFiles(workspaceId, addedFiles);
  }
  return true;
}

// ---------- Workspace-scoped files (images) ----------

function fileKey(workspaceId: WorkspaceId, fileId: FileId): string {
  return `${PREFIX.file}${workspaceId}:${fileId}`;
}

export async function getWorkspaceFiles(
  workspaceId: WorkspaceId,
  fileIds: FileId[],
): Promise<{ loadedFiles: BinaryFileData[]; erroredFiles: Map<FileId, true> }> {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();
  const keysToGet = fileIds.map((fid) => fileKey(workspaceId, fid));
  const results = await getMany(keysToGet, store);
  fileIds.forEach((fid, i) => {
    const data = results[i] as BinaryFileData | undefined;
    if (data) {
      loadedFiles.push({ ...data, lastRetrieved: Date.now() });
    } else {
      erroredFiles.set(fid, true);
    }
  });
  return { loadedFiles, erroredFiles };
}

export async function saveWorkspaceFiles(
  workspaceId: WorkspaceId,
  addedFiles: Map<FileId, BinaryFileData>,
): Promise<{
  savedFiles: Map<FileId, BinaryFileData>;
  erroredFiles: Map<FileId, BinaryFileData>;
}> {
  const savedFiles = new Map<FileId, BinaryFileData>();
  const erroredFiles = new Map<FileId, BinaryFileData>();
  await Promise.all(
    [...addedFiles].map(async ([id, fileData]) => {
      try {
        await set(fileKey(workspaceId, id), fileData, store);
        savedFiles.set(id, fileData);
      } catch (e: any) {
        console.error(e);
        erroredFiles.set(id, fileData);
      }
    }),
  );
  return { savedFiles, erroredFiles };
}

export async function clearWorkspaceFiles(
  workspaceId: WorkspaceId,
): Promise<void> {
  const allKeys = await keys(store);
  const prefix = `${PREFIX.file}${workspaceId}:`;
  const toDelete = (allKeys as string[]).filter((k) => k.startsWith(prefix));
  await Promise.all(toDelete.map((k) => del(k, store)));
}

/** List all file IDs stored for a workspace (for duplication). */
async function listWorkspaceFileIds(
  workspaceId: WorkspaceId,
): Promise<FileId[]> {
  const allKeys = await keys(store);
  const prefix = `${PREFIX.file}${workspaceId}:`;
  return (allKeys as string[])
    .filter((k) => k.startsWith(prefix))
    .map((k) => k.slice(prefix.length) as FileId);
}

/**
 * Duplicate a workspace in the same project (root). Copies scene and images.
 * Returns the new workspace or null if source not found.
 */
export async function duplicateWorkspace(
  sourceWorkspaceId: WorkspaceId,
  newName?: string,
): Promise<Workspace | null> {
  const source = await getWorkspace(sourceWorkspaceId);
  if (!source) {
    return null;
  }
  const name = newName?.trim() || `Copy of ${source.name}`;
  const created = await createWorkspace(source.projectId, null, name, {
    elements: source.elements,
    appState: source.appState,
  });
  const fileIds = await listWorkspaceFileIds(sourceWorkspaceId);
  if (fileIds.length > 0) {
    const { loadedFiles } = await getWorkspaceFiles(sourceWorkspaceId, fileIds);
    const filesMap = new Map<FileId, BinaryFileData>();
    fileIds.forEach((id, i) => {
      const data = loadedFiles[i];
      if (data) {
        filesMap.set(id, data);
      }
    });
    if (filesMap.size > 0) {
      await saveWorkspaceFiles(created.id, filesMap);
    }
  }
  return created;
}
